/**
 * VolatilityCard - ATR & Volatility Indicator
 *
 * Displays: ATR value, ATR %, volatility regime
 *
 * @version 1.0.0
 */

import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import IndicatorCard from '../IndicatorCard';

const VolatilityCard = memo(({ adxAnalysis, currentState }) => {
    const h4Atr = adxAnalysis?.h4?.atr || currentState?.volatility?.h4Atr;
    const d1Atr = adxAnalysis?.d1?.atr || currentState?.volatility?.d1Atr;
    const price = currentState?.price || 0;

    // Calculate ATR as percentage
    const h4AtrPercent = price > 0 && h4Atr ? ((h4Atr / price) * 100) : 0;
    const d1AtrPercent = price > 0 && d1Atr ? ((d1Atr / price) * 100) : 0;

    // Volatility regime based on ATR%
    const getVolatilityRegime = (atrPercent) => {
        if (atrPercent >= 0.5) return { label: 'HIGH', color: '#ef4444' };
        if (atrPercent >= 0.25) return { label: 'MEDIUM', color: '#fbbf24' };
        return { label: 'LOW', color: '#22c55e' };
    };

    const regime = getVolatilityRegime(h4AtrPercent);

    const summary = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* ATR Value */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#FAFAFA',
                        fontFamily: 'monospace'
                    }}
                >
                    {h4Atr?.toFixed(2) || '--'}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: '#71717A'
                    }}
                >
                    ATR
                </Typography>
            </Box>

            {/* Volatility Badge */}
            <Box
                sx={{
                    backgroundColor: `${regime.color}20`,
                    color: regime.color,
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '4px'
                }}
            >
                {regime.label} ({h4AtrPercent.toFixed(2)}%)
            </Box>
        </Box>
    );

    return (
        <IndicatorCard
            title="VOLATILITY"
            icon="📉"
            summary={summary}
            status={regime.label === 'HIGH' ? 'bearish' : regime.label === 'LOW' ? 'bullish' : 'neutral'}
            defaultExpanded={false}
        >
            {/* Expanded: Multi-TF ATR */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* H4 Row */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '4px'
                    }}
                >
                    <Typography sx={{ fontSize: '10px', color: '#A1A1AA', fontFamily: 'monospace' }}>
                        H4
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '11px', color: '#FAFAFA', fontWeight: 600, fontFamily: 'monospace' }}>
                            {h4Atr?.toFixed(2) || '--'}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: regime.color }}>
                            {h4AtrPercent.toFixed(2)}%
                        </Typography>
                    </Box>
                </Box>

                {/* D1 Row */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '4px'
                    }}
                >
                    <Typography sx={{ fontSize: '10px', color: '#A1A1AA', fontFamily: 'monospace' }}>
                        D1
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '11px', color: '#FAFAFA', fontWeight: 600, fontFamily: 'monospace' }}>
                            {d1Atr?.toFixed(2) || '--'}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: getVolatilityRegime(d1AtrPercent).color }}>
                            {d1AtrPercent.toFixed(2)}%
                        </Typography>
                    </Box>
                </Box>

                {/* Legend */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                        <Typography sx={{ fontSize: '9px', color: '#52525B' }}>&lt;0.25%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fbbf24' }} />
                        <Typography sx={{ fontSize: '9px', color: '#52525B' }}>0.25-0.5%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                        <Typography sx={{ fontSize: '9px', color: '#52525B' }}>&gt;0.5%</Typography>
                    </Box>
                </Box>
            </Box>
        </IndicatorCard>
    );
});

VolatilityCard.displayName = 'VolatilityCard';

export default VolatilityCard;
